import { observable, action, computed, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { validationContext, required, func } from 'validx'
import Team from 'iam/models/Team'
import TeamStore from 'iam/TeamStore'
import { Id } from 'types/definitions'
import { GetCurrentMember } from 'iam/SessionStore'
import { populateValidationContextFromError } from 'utils/error-util'
import { AlertFactory, AlertState } from 'alerts/Alert'

/**
 * State used for editing team info.
 */
export default class TeamDetailsViewModel {
  @observable name = ''
  @observable slug = ''
  @observable teamPurpose = ''
  @observable streetAddress = ''
  @observable city = ''
  @observable state = ''
  @observable zipCode = ''
  @observable country = ''

  constructor(
    public team: Team,
    private teamStore: TeamStore,
    private getCurrentMember: GetCurrentMember,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
    this.saveInfo = this.saveInfo.bind(this)
  }

  validation = validationContext(this, {
    name: [
      required('Team name is required'),
      func(({ value }) => {
        return value.length <= 50
      }, "Name can't be more than 50 characters"),
    ],
  })

  /**
   * Activates the state.
   */
  async activate() {
    this.set({
      name: this.team.name,
      slug: this.team.slug,
      teamPurpose: this.team.teamPurpose,
      streetAddress: this.team.address && this.team.address.streetAddress,
      zipCode: this.team.address && this.team.address.zipCode,
      city: this.team.address && this.team.address.city,
      state: this.team.address && this.team.address.state,
      country: this.team.address && this.team.address.country,
    })
  }

  @computed
  get hasChanges() {
    return (
      this.name !== this.team.name ||
      this.slug !== this.team.slug ||
      this.teamPurpose !== this.team.teamPurpose ||
      this.streetAddress !== this.team.address?.streetAddress ||
      this.city !== this.team.address?.city ||
      this.state !== this.team.address?.state ||
      this.country !== this.team.address?.country
    )
  }

  @computed
  get canEditDetails() {
    const currentMember = this.getCurrentMember()
    return this.team.owner.member.user.id === currentMember.user.id
  }

  /**
   * Sets the props on the state.
   *
   * @param {*} props
   */
  @action.bound
  set(props: Partial<TeamDetailsViewModel>) {
    Object.assign(this, props)
    return this
  }

  /**
   * Saves the team info.
   */
  saveInfo = task.resolved(async () => {
    // Trim the name.
    this.set({
      name: this.name ? this.name.trim() : '',
      teamPurpose: this.teamPurpose ? this.teamPurpose.trim() : '',
      streetAddress: this.streetAddress ? this.streetAddress.trim() : '',
      city: this.city ? this.city.trim() : '',
      state: this.state ? this.state.trim() : '',
      zipCode: this.zipCode ? this.zipCode.trim() : '',
      country: this.country ? this.country.trim() : '',
    })

    if (!this.validation.reset().validate().isValid) {
      return
    }

    try {
      await this.teamStore.updateTeam(Id.assert(this.team.id), {
        name: this.name,
        team_purpose: this.teamPurpose,
        address: {
          street_address: this.streetAddress,
          zip_code: this.zipCode,
          city: this.city,
          state: this.state,
          country: this.country,
        },
      })

      this.newAlert()
        .withState(AlertState.Success)
        .saying('Success!', 'Your changes were saved!')
        .autoDismiss()
        .show()
    } catch (error) {
      populateValidationContextFromError(this.validation, error, 'camel')
    }
  })
}
